// src/components/RouteErrorBoundary.js
import React from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw, Home, ArrowLeft } from 'lucide-react';

/**
 * A lightweight error boundary for individual routes.
 * Unlike the global ErrorBoundary, this allows users to navigate away
 * without refreshing the entire app.
 */
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Route Error:', error);

    // Send to Sentry
    Sentry.withScope((scope) => {
      scope.setExtras(errorInfo);
      scope.setTag('errorBoundary', 'route');
      scope.setTag('route', window.location.pathname);
      Sentry.captureException(error);
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoBack = () => {
    window.history.back();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const { fallback } = this.props;

      // Allow custom fallback
      if (fallback) {
        return fallback;
      }

      return (
        <div className="flex items-center justify-center min-h-[60vh] p-4">
          <div className="max-w-md w-full text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>

            <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
              This page encountered an error
            </h2>

            <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-6">
              Something went wrong loading this page. You can try again or navigate to a different page.
            </p>

            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg
                         bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900
                         text-sm font-medium hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>

              <button
                onClick={this.handleGoBack}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg
                         border border-zinc-300 dark:border-zinc-700
                         text-zinc-700 dark:text-zinc-300
                         text-sm font-medium hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Go Back
              </button>

              <button
                onClick={this.handleGoHome}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg
                         border border-zinc-300 dark:border-zinc-700
                         text-zinc-700 dark:text-zinc-300
                         text-sm font-medium hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
              >
                <Home className="w-4 h-4" />
                Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary;
